import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
  IconButton, Avatar, Grid, Link, Tooltip, Chip
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import SendIcon from '@mui/icons-material/Send';
import SettingsIcon from '@mui/icons-material/Settings';
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import RateReviewIcon from "@mui/icons-material/RateReview";

import CloseIcon from "@mui/icons-material/Close";
import { DataGrid } from '@mui/x-data-grid';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { api } from "../services/api";
import { useToast } from "../components/ToastContext";
import 'react-toastify/dist/ReactToastify.css';
import Layout from "../layout/layout";
import { useTheme } from "@mui/material/styles";
import SearchComponent from "../components/Search";
import { formatDistanceToNow } from 'date-fns';
import ApprovalFlow from '../pages/ApprovalFlow';
import DateTimePickerDialog from "../components/DateTimePicker";
import dayjs from "dayjs";
import CommentIcon from '@mui/icons-material/Comment';
import servicenow from '../assets/servicenow.png';
import RefreshIcon from "@mui/icons-material/Refresh";
import { evaluateAndReturn } from "../Utils/common";
import { calcLength } from "framer-motion";
import DynamicListeners from "./alb/DynamicListeners";
import DynamicRules from "./sgrule/DynamicRules";
import CircleIcon from '@mui/icons-material/Circle';
import { Bold } from "lucide-react";

// ========== CUSTOM HOOKS FOR STATE CONSOLIDATION ==========

// Hook to manage all dialog open/close states
const useDialogStates = () => {
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [openApproveDialog, setOpenApproveDialog] = useState(false);
  const [openUnApproveDialog, setOpenUnApproveDialog] = useState(false);
  const [openCommentsDialog, setOpenCommentsDialog] = useState(false);
  const [openApprovalFlow, setOpenApprovalFlow] = useState(false);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [gitDetailsDialogOpen, setGitDetailsDialogOpen] = useState(false);
  const [searchBoxVisible, setSearchBoxVisible] = useState(false);

  return {
    openViewDialog, setOpenViewDialog,
    openApproveDialog, setOpenApproveDialog,
    openUnApproveDialog, setOpenUnApproveDialog,
    openCommentsDialog, setOpenCommentsDialog,
    openApprovalFlow, setOpenApprovalFlow,
    scheduleDialogOpen, setScheduleDialogOpen,
    dialogOpen, setDialogOpen,
    gitDetailsDialogOpen, setGitDetailsDialogOpen,
    searchBoxVisible, setSearchBoxVisible
  };
};

// Hook to manage ticket and request data
const useTicketData = () => {
  const [ticketRequests, setTicketRequests] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [resourceDetails, setResourceDetails] = useState([]);
  const [viewResponse, setViewResponse] = useState([]);
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [comments, setComments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState(['completed']);
  const [selectedSnowStatuses, setSelectedSnowStatuses] = useState([]);

  return {
    ticketRequests, setTicketRequests,
    selectedTicket, setSelectedTicket,
    resourceDetails, setResourceDetails,
    viewResponse, setViewResponse,
    selectedRequestId, setSelectedRequestId,
    comments, setComments,
    searchTerm, setSearchTerm,
    selectedStatuses, setSelectedStatuses,
    selectedSnowStatuses, setSelectedSnowStatuses,
  };
};

// Hook to manage approval states
const useApprovalStates = () => {
  const [approvalSteps, setApprovalSteps] = useState([]);
  const [approvalTicket, setApprovalTicket] = useState(null);
  const [popoverSteps, setPopoverSteps] = useState([]);
  const [comment, setComment] = useState("");
  const [approver, setApprover] = useState("");
  const [selectedScheduleTicket, setSelectedScheduleTicket] = useState(null);
  const [scheduleValue, setScheduleValue] = useState(dayjs());

  return {
    approvalSteps, setApprovalSteps,
    approvalTicket, setApprovalTicket,
    popoverSteps, setPopoverSteps,
    comment, setComment,
    approver, setApprover,
    selectedScheduleTicket, setSelectedScheduleTicket,
    scheduleValue, setScheduleValue,
  };
};

// Hook to manage loading states
const useLoadingStates = () => {
  const [loadingComments, setLoadingComments] = useState(false);
  const [mainLoading, setMainLoading] = useState(true);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [popoverLoading, setPopoverLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingStates, setLoadingStates] = useState({});

  return {
    loadingComments, setLoadingComments,
    mainLoading, setMainLoading,
    refreshLoading, setRefreshLoading,
    approvalLoading, setApprovalLoading,
    scheduleLoading, setScheduleLoading,
    popoverLoading, setPopoverLoading,
    loading, setLoading,
    loadingStates, setLoadingStates,
  };
};

// Hook to manage UI states (popover, details, git)
const useUIStates = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [hoveredTicket, setHoveredTicket] = useState(null);
  const [snowDetails, setSnowDetails] = useState(null);
  const [gitDetails, setGitDetails] = useState({
    gitStatus: '',
    run_Id: '',
    gitWorkflowUrl: '',
    gitActionResouceHandlerMessage: '',
    gitActionMessageSummary: '',
  });
  const [pendingCount, setPendingCount] = useState(0);
  const [notificationCount, setNotificationCount] = useState(0);

  return {
    anchorEl, setAnchorEl,
    hoveredTicket, setHoveredTicket,
    snowDetails, setSnowDetails,
    gitDetails, setGitDetails,
    pendingCount, setPendingCount,
    notificationCount, setNotificationCount,
  };
};

export default function TicketRequests() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark'; // Master check for dark mode
  const toast = useToast();
  const userId = sessionStorage.getItem('userId');
  const userRole = sessionStorage.getItem('userRole');
  const username = sessionStorage.getItem('username');

  const pageTitle = useMemo(() => {
    try {
      const menus = JSON.parse(sessionStorage.getItem('userMenus') || '[]');
      const matchedMenu = menus.find(
        (m) => m.path?.toLowerCase() === '/provision-status'
      );
      return matchedMenu?.label ?? null;
    } catch {
      return null;
    }
  }, []);

  // Consolidate state management with custom hooks
  const dialogs = useDialogStates();
  const ticketData = useTicketData();
  const approvalStates = useApprovalStates();
  const loadingState = useLoadingStates();
  const uiState = useUIStates();

  // Destructure all state variables and setters from custom hooks
  const { openViewDialog, setOpenViewDialog, openApproveDialog, setOpenApproveDialog, openUnApproveDialog, setOpenUnApproveDialog, openCommentsDialog, setOpenCommentsDialog, openApprovalFlow, setOpenApprovalFlow, scheduleDialogOpen, setScheduleDialogOpen, dialogOpen, setDialogOpen, gitDetailsDialogOpen, setGitDetailsDialogOpen, searchBoxVisible } = dialogs;
  const { ticketRequests, setTicketRequests, selectedTicket, setSelectedTicket, resourceDetails, setResourceDetails, viewResponse, setViewResponse, setSelectedRequestId, comments, setComments, searchTerm, setSearchTerm, selectedStatuses, setSelectedStatuses, selectedSnowStatuses, setSelectedSnowStatuses } = ticketData;
  const { approvalSteps, approvalTicket, comment, setComment, approver, setApprover, selectedScheduleTicket, setSelectedScheduleTicket, scheduleValue, setScheduleValue } = approvalStates;
  const { loadingComments, setLoadingComments, mainLoading, setMainLoading, refreshLoading, setRefreshLoading, scheduleLoading, setScheduleLoading, loading, setLoading, loadingStates } = loadingState;
  const { snowDetails, setSnowDetails, gitDetails, setGitDetails, pendingCount, setPendingCount } = uiState;
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [totalCount, setTotalCount] = useState(0);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, 400);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [debouncedSearchTerm]);

  // Open dialog for SRE deploy action
  const handleOpenScheduleDialog = (ticket) => {
    setSelectedScheduleTicket(ticket);
    setScheduleValue(dayjs());
    setScheduleDialogOpen(true);
  };

  const handleCloseScheduleDialog = () => {
    setScheduleDialogOpen(false);
    setSelectedScheduleTicket(null);
    setScheduleValue(dayjs());
  };

  const handleConfirmSchedule = async () => {
    setScheduleLoading(true);
    try {
      await api.post(`/api/Resources/deployment/${selectedScheduleTicket.resourceId}/?userId=${userId}`, {
        scheduledTime: scheduleValue.toISOString(),
      });
      toast.success('Deployment scheduled!');
      await fetchRequests();
      handleCloseScheduleDialog();
    } catch (error) {
      toast.error(error.message || 'Error scheduling deployment');
    } finally {
      setScheduleLoading(false);
    }
  };

  //  UPDATED: Dynamic Colors for Git Status indicators 
  const runningIndicatorColors = {
    completed: isDark ? '#4caf50' : '#2E7D32',
    queued: isDark ? '#64b5f6' : '#1E5BD7',
    failed: isDark ? '#e57373' : '#7A1F1F',
    'in progress': isDark ? '#ffb74d' : '#E68A00',
    null: isDark ? '#b0bec5' : '#455a64',
  };

  const getStatusKey = (status) => {
    if (!status) return 'null';
    const lower = status.toLowerCase();
    if (lower.includes('completed')) return 'completed';
    if (lower.includes('failed')) return 'failed';
    if (status.includes('Queued')) return 'queued';
    if (lower.includes('in progress') || status.includes('In Progress')) return 'in progress';
    return 'null';
  };

  const getColorByStatus = (status) => {
    return runningIndicatorColors[getStatusKey(status)];
  };

  const fetchComments = async (resourceReqId) => {
    setLoadingComments(true);
    try {
      const response = await api.get(`/api/Resources/comments`, {
        params: { resourceReqId },
        headers: { 'X-CORRELATION-ID': 'e1885f6b-cddf-4e2a-859a-d3566ba3a99e' }
      });
      setComments(response.data);
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setLoadingComments(false);
    }
  };

  const handleSearchChange = (term) => {
    setSearchTerm(term);
  };

  const handleStatusToggle = (statusKey) => {
    setSelectedStatuses((prev) =>
      prev.includes(statusKey)
        ? prev.filter((s) => s !== statusKey)
        : [...prev, statusKey]
    );
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleSnowStatusToggle = (status) => {
    setSelectedSnowStatuses((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status]
    );
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const fetchRequests = async (isRefresh = false) => {
    const activeAccountId = sessionStorage.getItem('activeAccountId');
    let accountId = activeAccountId;

    if (!accountId || accountId === 'null' || accountId === 'undefined') {
      accountId = '';
    }

    if (isRefresh) {
      setRefreshLoading(true);
    } else {
      setMainLoading(true);
    }

    try {
      const response = await api.get('/api/Resources/getAllRequests', {
        params: {
          userId: userId,
          resourceType: 0,
          activeaccountId: accountId,
          pageNumber: paginationModel.page + 1,
          pageSize: paginationModel.pageSize,
          searchText: debouncedSearchTerm,
        },
        paramsSerializer: params => {
          return Object.entries(params)
            .map(([key, val]) => `${encodeURIComponent(key)}=${encodeURIComponent(val ?? '')}`)
            .join('&');
        },
        headers: {
          'X-CORRELATION-ID': 'd90c25f4-ec27-485d-ba8a-c29a69874430'
        }
      });

      const headers = response.headers;
      const totalCountHeader =
        headers?.["x-total-count"] ??
        headers?.get?.("x-total-count") ??
        headers?.["X-Total-Count"];

      const rows = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.data)
          ? response.data.data
          : Array.isArray(response.data?.items)
            ? response.data.items
            : [];

      const parsedTotalCount = Number(totalCountHeader);
      const fallbackTotalCount = Number(response.data?.totalCount ?? response.data?.count ?? rows.length);

      if (Number.isFinite(parsedTotalCount) && parsedTotalCount >= 0) {
        setTotalCount(parsedTotalCount);
      } else if (Number.isFinite(fallbackTotalCount) && fallbackTotalCount >= 0) {
        setTotalCount(fallbackTotalCount);
      } else {
        setTotalCount(rows.length);
      }

      // 1. DEDUPLICATE THE BACKEND DATA
      const uniqueData = rows.filter((request, index, self) =>
        index === self.findIndex((t) => {
          if (request.resourceId && t.resourceId) {
            return t.resourceId === request.resourceId;
          }
          return t.snoW_CRNo === request.snoW_CRNo && t.resourceNameConstraint === request.resourceNameConstraint;
        })
      );

      // 2. PREPARE DATA FOR THE GRID
      const requests = uniqueData.map((request, index) => ({
        ...request,
        isDeployed: request.isDeployed,
        uniqueGridId: request.resourceId
          ? `${request.resourceId}-${index}`
          : `fallback-${request.snoW_CRNo}-${index}-${Math.random()}`
      }));

      setTicketRequests(requests);

      // 3. CALCULATE PENDING COUNT
      const count = userRole === 'SRE'
        ? requests.filter(request => request.status === "Approved" && !request.isDeployed).length
        : requests.filter(request =>
          request.currentVerifier === username && request.loginUserStatus === "Pending"
        ).length;

      setPendingCount(count);

    } catch (error) {
      console.error('Error fetching requests:', error);
      toast.error(error.message || 'Failed to fetch ticket requests');
    } finally {
      if (isRefresh) {
        setRefreshLoading(false);
      } else {
        setMainLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchRequests();
  }, [paginationModel.page, paginationModel.pageSize, debouncedSearchTerm]);

  const formatDateTime = (date) => {
    if (!date) return 'N/A';

    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  };

  const getCountdownText = (date) => {
    if (!date) return '';

    const dueDate = new Date(date);
    const now = new Date();

    const isExpired = dueDate < now;

    const diffMs = Math.abs(dueDate - now);

    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    const hours = Math.floor(
      (diffMs % (1000 * 60 * 60 * 24)) /
      (1000 * 60 * 60)
    );

    const minutes = Math.floor(
      (diffMs % (1000 * 60 * 60)) /
      (1000 * 60)
    );

    const timeText = [
      days ? `${days}d` : '',
      hours ? `${hours}h` : '',
      minutes ? `${minutes}m` : '',
    ]
      .filter(Boolean)
      .join(' ');

    return isExpired
      ? `Expired ${timeText} ago`
      : `Expires in ${timeText}`;
  };

  const filteredRequests = useMemo(() => {
    const searchLower = debouncedSearchTerm.toLowerCase();
    return ticketRequests.filter((request) => {
      if (!request.isResourceAddedFromCMP) return false;
      const currentStatusKey = getStatusKey(request.gitStatus);
      if (selectedStatuses.length > 0 && !selectedStatuses.includes(currentStatusKey)) {
        return false;

      }
      const snowStatus =
        request.snoW_CRStatus?.toLowerCase() || "not requested";

      if (
        selectedSnowStatuses.length > 0 &&
        !selectedSnowStatuses.includes(snowStatus)
      ) {
        return false;
      }

      const resourceTypeDisplay =
        request.operationSupport
          ? `${request.resourceName} - ${request.operationSupport}`
          : (request.resourceName || '');

      const dueDateFormatted = request.dueDate ? formatDateTime(request.dueDate).toLowerCase() : '';
      const dueDateCountdown = request.dueDate ? getCountdownText(request.dueDate).toLowerCase() : '';

      const matchesSearch = !searchLower || (
        (request.snoW_CRNo?.toLowerCase() || '').includes(searchLower) ||
        (request.applicationName?.toLowerCase() || '').includes(searchLower) ||
        (request.requester?.toLowerCase() || '').includes(searchLower) ||
        (request.resourceName?.toLowerCase() || '').includes(searchLower) ||
        (request.resourceNameConstraint?.toLowerCase() || '').includes(searchLower) ||
        (request.gitStatus?.toLowerCase() || '').includes(searchLower) ||
        (request.snoW_CRStatus?.toLowerCase() || '').includes(searchLower) ||
        (request.state?.toLowerCase() || '').includes(searchLower) ||
        dueDateFormatted.includes(searchLower) ||
        dueDateCountdown.includes(searchLower) ||
        (request.serviceNowCrNo?.toLowerCase() || '').includes(searchLower) ||
        (request.awsInstanceId?.toLowerCase() || '').includes(searchLower) ||
        (request.price?.toString() || '').includes(searchLower) ||
        (resourceTypeDisplay.toLowerCase()).includes(searchLower)
      );

      return matchesSearch;
    });
  }, [ticketRequests, debouncedSearchTerm, selectedStatuses, selectedSnowStatuses]);

  const handleRefreshResources = async () => {
    try {
      await fetchRequests(true);
    } catch (error) {
      console.error("Refresh failed", error);
      toast.error("Failed to refresh records");
    }
  };


  const handleViewClick = async (ticket) => {
    try {
      const response = await api.get(`/api/Resources/viewResourceRequest/${ticket.resourceId}`, {
        headers: { 'X-CORRELATION-ID': '0d89a8fe-6a3f-46e6-8bde-72daf869b07c' }
      });
      setResourceDetails(response.data.resourceRequestDetails);
      setViewResponse(response.data);
      setSelectedTicket(ticket);
      setOpenViewDialog(true);
    } catch (error) {
      console.error('Error fetching resource details:', error);
    }
  };

  const handleSnowDetails = async (ticket) => {
    setLoading(true);
    try {
      const response = await api.get(`/api/Resources/getSnowDetails/${ticket.snoW_CRNo}`, {
        headers: { 'X-CORRELATION-ID': '0d89a8fe-6a3f-46e6-8bde-72daf869b07c' }
      });

      setSnowDetails(response.data);
      setDialogOpen(true);

    } catch (error) {
      console.error('Error fetching resource details:', error);
    } finally {
      setLoading(false);
    }
  }

  const handleCloseViewDialog = () => {
    setOpenViewDialog(false);
    setSelectedTicket(null);
    setResourceDetails([]);
  };

  const handleCloseUnApproveDialog = (ticket) => {
    setSelectedTicket(ticket);
    setOpenUnApproveDialog(false);
    setComment("");
    setApprover("");
  };

  const handleCloseApproveDialog = () => {
    setOpenApproveDialog(false);
    setSelectedTicket(null);
  };

  const handleCommentsClick = (request) => {
    setSelectedRequestId(request.resourceId);
    fetchComments(request.resourceId);
    setOpenCommentsDialog(true);
  };

  const handleCloseCommentsDialog = () => {
    setOpenCommentsDialog(false);
    setComments([]);
  };

  const handleGitIconClick = async (row) => {
    let message = row.gitActionResouceHandlerMessage;

    setGitDetails({
      gitStatus: row.gitStatus,
      run_Id: row.run_Id,
      gitActionResouceHandlerMessage: message,
      gitActionMessageSummary: row.gitActionMessageSummary,
    });
    setGitDetailsDialogOpen(true);
  };

  const formatLabel = (value) => {
    if (!value) return "";

    return value
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const baseColumns = [
    {
      field: 'snoW_CRNo', headerName: 'GSnow No#', minWidth: 100, flex: 1,
      renderCell: (params) => {
        const isQueued = params.row.snoW_CRNo === 'Queued';

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', marginTop: '7px', minHeight: '40px', }}>
            {isQueued ? (
              //  UPDATED: Replaced #000000 with theme-aware color
              <Typography variant="body2" sx={{
                flexGrow: 1,
                color: isDark ? 'text.secondary' : '#000000',
              }}>
                {params.value}
              </Typography>
            ) : (
              <Typography
                variant="body2"
                sx={{
                  flexGrow: 1,
                  color: isDark ? '#90caf9' : '#1976d2',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                }}
                onClick={() => handleSnowDetails(params.row)}
              >
                {params.value}
              </Typography>
            )}
          </Box>
        )
      }
    },
    {
      field: 'snoW_CRStatus',
      headerName: 'GSnow Approval Status',
      minWidth: 180,
      flex: 1,

      renderCell: (params) => {
        const status = params.value?.trim() || "Not Requested";

        //  UPDATED: Replaced Hardcoded Chips with Dark Mode aware chips
        const statusConfig = {
          requested: {
            bg: isDark ? "rgba(237, 108, 2, 0.16)" : "#fff4e5",
            color: isDark ? "#ffb74d" : "#ed6c02",
            icon: <AccessTimeIcon sx={{ fontSize: 14 }} />,
          },
          approved: {
            bg: isDark ? "rgba(46, 125, 50, 0.16)" : "#edf7ed",
            color: isDark ? "#81c784" : "#2e7d32",
            icon: <CheckCircleIcon sx={{ fontSize: 14 }} />,
          },
          rejected: {
            bg: isDark ? "rgba(211, 47, 47, 0.16)" : "#fdecea",
            color: isDark ? "#e57373" : "#d32f2f",
            icon: <CancelIcon sx={{ fontSize: 14 }} />,
          },
          "not requested": {
            bg: isDark ? "rgba(255, 255, 255, 0.08)" : "#f5f5f5",
            color: isDark ? "#9e9e9e" : "#757575",
            icon: <RemoveCircleOutlineIcon sx={{ fontSize: 14 }} />,
          },
        };

        const key = status.toLowerCase();
        const config = statusConfig[key] || statusConfig["not requested"];

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.7,
              px: 1.5,
              py: 0.5,
              borderRadius: "16px",
              bgcolor: config.bg,
              color: config.color,
              width: "fit-content",
              maxWidth: "100%",
              marginTop: "15px",
              whiteSpace: "nowrap",
            }}
          >
            {config.icon}
            <Typography
              sx={{
                fontSize: "0.78rem",
                fontWeight: 500,
                lineHeight: 1,
                textTransform: "capitalize",
              }}
            >
              {formatLabel(status)}
            </Typography>
          </Box>
        );
      }
    },
    {
      field: 'state',
      headerName: 'GSnow State',
      minWidth: 150,
      flex: 1,

      renderCell: (params) => {
        const state = params.value?.trim() || "NA";

        //  UPDATED: Replaced Hardcoded Chips with Dark Mode aware chips
        const stateConfig = {
          requested: {
            bg: isDark ? "rgba(21, 101, 192, 0.16)" : "#eef4ff",
            color: isDark ? "#64b5f6" : "#1565c0",
            icon: <SendIcon sx={{ fontSize: 14 }} />,
          },
          implement: {
            bg: isDark ? "rgba(123, 31, 162, 0.16)" : "#f3e8ff",
            color: isDark ? "#ba68c8" : "#7b1fa2",
            icon: <SettingsIcon sx={{ fontSize: 14 }} />,
          },
          completed: {
            bg: isDark ? "rgba(46, 125, 50, 0.16)" : "#edf7ed",
            color: isDark ? "#81c784" : "#2e7d32",
            icon: <CheckCircleIcon sx={{ fontSize: 14 }} />,
          },
          hold: {
            bg: isDark ? "rgba(249, 168, 37, 0.16)" : "#fff8e1",
            color: isDark ? "#ffd54f" : "#f9a825",
            icon: <AccessTimeIcon sx={{ fontSize: 14 }} />,
          },
          canceled: {
            bg: isDark ? "rgba(255, 255, 255, 0.08)" : "#f5f5f5",
            color: isDark ? "#9e9e9e" : "#757575",
            icon: <CancelIcon sx={{ fontSize: 14 }} />,
          },
          closed: {
            bg: isDark ? "rgba(46, 125, 50, 0.16)" : "#e8f5e9",
            color: isDark ? "#81c784" : "#2e7d32",
            icon: <TaskAltIcon sx={{ fontSize: 14 }} />,
          },
          review: {
            bg: isDark ? "rgba(2, 119, 189, 0.16)" : "#e3f2fd",
            color: isDark ? "#4fc3f7" : "#0277bd",
            icon: <RateReviewIcon sx={{ fontSize: 14 }} />,
          },
        };

        const key = state.toLowerCase();
        const config = stateConfig[key] || {
          bg: isDark ? "rgba(255, 255, 255, 0.08)" : "#f5f5f5",
          color: isDark ? "#9e9e9e" : "#757575",
          icon: null,
        };

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.7,
              px: 1.5,
              py: 0.5,
              borderRadius: "16px",
              bgcolor: config.bg,
              color: config.color,
              width: "fit-content",
              maxWidth: "100%",
              marginTop: "15px",
            }}
          >
            {config.icon}
            <Typography
              sx={{
                fontSize: "0.78rem",
                fontWeight: 500,
                lineHeight: 1,
                textTransform: "capitalize",
              }}
            >
              {formatLabel(state)}
            </Typography>
          </Box>
        );
      }
    },
    {
      field: 'dueDate',
      headerName: 'GSnow Approval Due Date',
      minWidth: 240,
      flex: 1.8,
      renderCell: (params) => {
        const dueDate = params.value ? new Date(params.value) : null;
        const now = new Date();

        const isExpired = dueDate && dueDate < now;
        const isClosed = params.row.state?.toLowerCase() === 'closed';
        const formattedDate = formatDateTime(dueDate);
        const countdownText = getCountdownText(dueDate);

        //  UPDATED: Dynamic Due Date colors
        return (
            <Box
              title={dueDate ? countdownText : 'No due date available'}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                width: '100%',
                height: '100%',
              }}
            >
              <AccessTimeIcon
                sx={{
                  fontSize: '1rem',
                  color: isClosed ? 'text.disabled' : (!dueDate ? 'text.secondary' : (isExpired ? 'text.disabled' : 'warning.main')),
                }}
              />

              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: 'nowrap',
                    color: isClosed ? 'text.disabled' : (!dueDate ? 'text.secondary' : (isExpired ? 'text.secondary' : 'warning.main')),
                    fontWeight: isExpired ? 400 : 600,
                    lineHeight: 1.2,
                  }}
                >
                  {formattedDate}
                </Typography>

                {dueDate && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: isClosed ? 'text.disabled' : 'text.secondary',
                      fontSize: '0.72rem',
                      lineHeight: 1.1,
                    }}
                  >
                    {countdownText}
                  </Typography>
                )}
              </Box>
            </Box>
        );
      },
    },
    {
      field: 'gitStatus',
      headerName: 'Git Status',
      minWidth: 180,
      flex: 1,
      renderCell: (params) => {
        const { gitStatus, run_Id, gitWorkflowUrl, gitActionResouceHandlerMessage } = params.row;

        const hasStatus = (gitStatus && gitStatus.trim() !== '');
        const hasRunId = (run_Id && run_Id !== 0);
        const showSingleDash = !hasStatus && !hasRunId;
        const isFailed = gitStatus?.toLowerCase() === "failed";
        const isCompleted = gitStatus?.toLowerCase()?.includes("completed");

        const getColor = (status) => {
          if (!status) return runningIndicatorColors['null'];
          const lower = status.toLowerCase();

          if (lower.includes("completed")) return runningIndicatorColors['completed'];
          if (lower.includes("failed")) return runningIndicatorColors['failed'];
          if (status.includes("Queued")) return runningIndicatorColors['queued'];
          if (status.includes("In Progress")) return runningIndicatorColors['in progress'];

          return runningIndicatorColors['null'];
        };

        const runIdContent = `Run ID: ${run_Id}`;
        const runIdDisplay = gitWorkflowUrl ? (
          <Link
            href={gitWorkflowUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: isDark ? '#90caf9' : theme.palette.primary.main,
              fontSize: '11px',
              textDecoration: 'underline',
              lineHeight: 1.1,
            }}
          >
            {runIdContent}
          </Link>
        ) : (
          <Typography
            variant="body2"
            sx={{
              fontSize: '11px',
              color: 'text.secondary',
              lineHeight: 1.1,
            }}
          >
            {runIdContent}
          </Typography>
        );

        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
              py: 1,
              minHeight: '52px',
              gap: 0,
            }}
          >

            {showSingleDash && (
              //  UPDATED: Made fallback dash dynamic instead of hardcoded
              <Typography
                variant="body2"
                sx={{ fontSize: '12px', fontWeight: 'bold', color: isDark ? 'text.secondary' : getColor(gitStatus), lineHeight: 1 }}
              >
                -
              </Typography>
            )}


            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '12px',
                    fontWeight: 'bold',
                    color: getColorByStatus(gitStatus),
                    lineHeight: 1,
                  }}
                >
                  {gitStatus}
                </Typography>

                {hasRunId ? (
                  runIdDisplay
                ) : (
                  hasStatus && (
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '11px',
                        color: 'text.secondary',
                        lineHeight: 1.1,
                      }}
                    >
                      {"-"}
                    </Typography>
                  )
                )}
              </Box>

              {(isFailed || isCompleted) && (
                <IconButton
                  size="small"
                  title={gitActionResouceHandlerMessage || ''}
                  onClick={() => handleGitIconClick(params.row)}
                  sx={{
                    color: getColorByStatus(gitStatus),
                    p: 0.5,
                    alignSelf: 'center',
                  }}
                >
                  {isFailed ? (
                    <WarningAmberIcon sx={{ fontSize: 16 }} />
                  ) : (
                    <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                  )}
                </IconButton>
              )}
            </Box>
          </Box>
        );
      }
    },

    {
      field: 'resourceNameConstraint',
      headerName: 'Resource Name',
      minWidth: 225,
      flex: 1.5,

      renderCell: (params) => {
        const text = params.value || '';
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              height: '100%',
              gap: 1,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                flex: 1,
                whiteSpace: 'pre-line',
                wordBreak: 'break-word',
                lineHeight: 1.3,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {text}
            </Typography>

            <IconButton
              size="small"
              onClick={() => handleViewClick(params.row)}
              sx={{
                flexShrink: 0,
              }}
            >
              <VisibilityIcon
                sx={{
                  color: isDark ? '#90caf9' : '#1976d2',
                  fontSize: '1rem',
                }}
              />
            </IconButton>
          </Box>
        );
      },
    },

    {
      field: 'awsInstanceId',
      headerName: 'Resource Id',
      minWidth: 200,
      flex: 1.5,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2">
            {params.value || '-'}
          </Typography>
        </Box>
      )
    },
    {
      field: 'resourceName',
      headerName: 'Resource Type',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => {
        const isCMP = params.row.isResourceAddedFromCMP;
        const gitStatus = params.row.gitStatus;

        const displayName =
          params.value?.toLowerCase() === "security groups"
            ? (
              params.row.operationSupport
                ? "SG - Rule Operation"
                : params.value
            )
            : (
              params.row.operationSupport
                ? `${params.value} - ${params.row.operationSupport}`
                : params.value
            );

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              borderRadius: "12px",
              overflow: "hidden",
              height: 26,
              backgroundColor: getColorByStatus(gitStatus),
              color: "#ffffff",
              minWidth: 120,
              maxWidth: 200,
              mt: 2.5,
              transition: "background-color 0.3s ease",
            }}
          >
            <Box
              sx={{
                flex: 1,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                fontSize: "0.70rem",
                height: "100%",
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap",
                px: 3,
                mt: 0.3,
              }}
            >
              {displayName}
            </Box>

            {isCMP && (
              <>
                <Box
                  sx={{
                    width: "1px",
                    backgroundColor: "rgba(255,255,255,0.5)",
                    height: "60%",
                  }}
                />

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "0.65rem",
                    fontWeight: 600,
                    height: "100%",
                    px: 1,
                    mt: 0.25,
                    mx: 0.2,
                    whiteSpace: "nowrap",
                  }}
                >
                  CMP
                </Box>
              </>
            )}
          </Box>
        );
      },
    },
    {
      field: 'applicationName',
      headerName: 'Application Name',
      minWidth: 200,
      flex: 1.8,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', height: '100%' }}>
          <Typography
            variant="body2"
            sx={{
              flexGrow: 1,
            }}
          >
            {params.value}
          </Typography>
        </Box>
      ),
    },

  ]

  const deployColumn = {
    field: 'deploy',
    headerName: 'Deploy',
    flex: 1,
    width: 190,
    renderCell: (params) => (
      <Button
        variant="contained"
        disabled={
          params.row.isDeployed ||
          params.row.status !== "Approved" ||
          loadingStates[params.row.resourceId]
        }
        onClick={() => handleOpenScheduleDialog(params.row)}
      >
        {params.row.isDeployed ? "Scheduled Deployment" : "Schedule Deploy"}
        {loadingStates[params.row.resourceId] && <CircularProgress size={24} sx={{ ml: 1 }} />}
      </Button>
    )
  };

  const getColumnsForRole = (role) => {
    const baseColsWithRequester = [
      ...baseColumns,
      {
        field: 'requester', headerName: 'Requester', minWidth: 120, flex: 1,

        renderCell: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
              {params.value}
            </Typography>
          </Box>
        )
      },
    ];

    if (role === 'SRE') {
      return [
        ...baseColumns,
        deployColumn,
        {
          field: 'comments',
          headerName: 'Comments',
          width: 100,
          renderCell: (params) => (
            <Box sx={{ display: 'flex', mt: '7px' }}>
              <Button variant="contained" sx={{ minWidth: "36px" }} onClick={() => handleCommentsClick(params.row)}><CommentIcon /></Button>
            </Box>
          )
        },
      ];
    }
    // if (role !== 'Client') {
    //   return baseColsWithRequester;
    // }
    return baseColumns;
  };

  const columns = getColumnsForRole(userRole);


  const groupedDetails = resourceDetails?.reduce((acc, detail) => {
    if (!acc[detail.tabName]) {
      acc[detail.tabName] = [];
    }
    acc[detail.tabName].push(detail);
    return acc;
  }, {});


  const renderRunId = (runId, workflowUrl) => {
    if (!runId) return <Typography variant="body2">-</Typography>;
    if (workflowUrl) {
      return (
        <Link href={workflowUrl} target="_blank" rel="noopener noreferrer" underline="hover">
          {runId}
        </Link>
      );
    }
    return <Typography variant="body2">{runId}</Typography>;
  };

  const gridLoading = refreshLoading || mainLoading;

  // 1. Define the table content outside the return statement
  const tableContent = (
      <DataGrid
        rows={filteredRequests}
        columns={columns}
        loading={gridLoading}
        rowHeight={65}
        scrollbarSize={12}
        getRowId={(row) => row.uniqueGridId}
        pagination
        paginationMode="server"
        rowCount={totalCount}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[25, 50, 100]}
        disableRowSelectionOnClick
        hideFooterSelectedRowCount

        sx={{
          border: "none",
          bgcolor: (t) => t.palette.mode === "dark" ? "background.paper" : "#ffffff",

          // Completely remove all borders
          "--DataGrid-rowBorderColor": "transparent",

          // 1. Hide the filler (white space on the right)
          "& .MuiDataGrid-filler": { display: "none" },

          // 2. Column Headers
          "& .MuiDataGrid-columnHeaders": {
            bgcolor: (t) => t.palette.mode === "dark" ? "background.default" : "#ffffff",
            color: "text.primary",
            borderBottom: "none",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: "bold !important",
          },

          "& .MuiDataGrid-columnSeparator": {
            color: (t) => t.palette.mode === "dark" ? "rgba(255, 255, 255, 0.11)" : "rgba(0, 0, 0, 0.2)",
          },

          "& .MuiDataGrid-columnHeader .MuiSvgIcon-root": {
            color: (t) => t.palette.mode === "dark" ? "rgba(255, 255, 255, 0.11)" : "rgba(0, 0, 0, 0.2)",
          },

          // 3. Cells
          "& .MuiDataGrid-cell": {
            color: "text.primary",
            borderBottom: "none !important",
          },

          // 4. Striped Rows
          "& .MuiDataGrid-row:nth-of-type(even)": {
            bgcolor: (t) => t.palette.mode === "dark" ? "rgba(255, 255, 255, 0.02)" : "#fafafa",
          },
          "& .MuiDataGrid-row:nth-of-type(odd)": {
            bgcolor: (t) => t.palette.mode === "dark" ? "transparent" : "#ffffff",
          },

          // 5. Hover State
          "& .MuiDataGrid-row:hover": {
            bgcolor: (t) => t.palette.mode === "dark" ? "rgba(255, 255, 255, 0.08) !important" : "#e8f0fe !important",
          },

          // 6. Selected State
          "& .MuiDataGrid-row.Mui-selected": {
            bgcolor: (t) => t.palette.mode === "dark" ? "rgba(144, 202, 249, 0.16) !important" : "#e3f2fd !important",
          },
          "& .MuiDataGrid-row.Mui-selected:hover": {
            bgcolor: (t) => t.palette.mode === "dark" ? "rgba(144, 202, 249, 0.24) !important" : "#d0eafb !important",
          },

          // 7. Footer
          "& .MuiDataGrid-footerContainer": {
            bgcolor: (t) => t.palette.mode === "dark" ? "background.default" : "#ffffff",
            borderTop: "none",
            color: "text.primary",
          },

          // 8. Floating Actions (Hover Icons)
          "& .floating-actions, & .row-actions": {
            backgroundColor: (t) => t.palette.mode === "dark" ? "rgba(40, 40, 40, 0.9)" : "#e8f0fe",
            flex: 1,
            position: "absolute",
            top: "50%",
            transform: "translateY(-50%)",
            display: "flex",
            gap: "6px",
            opacity: 0,
            pointerEvents: "none",
            transition: "opacity 0.2s ease-in-out",
            padding: "4px 8px",
            borderRadius: "4px",
          },
          "& .MuiDataGrid-row:hover .floating-actions, & .MuiDataGrid-row:hover .row-actions, & .MuiDataGrid-row.Mui-selected .floating-actions, & .MuiDataGrid-row.Mui-selected .row-actions": {
            opacity: 1,
            pointerEvents: "auto",
          },

          // Hide the physical column space for the floating actions
          "& .MuiDataGrid-columnHeader[data-field='__floating_actions'], & .MuiDataGrid-columnHeader[data-field='actions']": {
            display: "none",
          },
          "& .MuiDataGrid-cell[data-field='__floating_actions'], & .MuiDataGrid-cell[data-field='actions']": {
            overflow: "visible !important",
            width: "0px !important",
            padding: 0,
            borderBottom: "none !important",
          },
        }}
      />
    );

  // 2. The updated return block
  return (
    <Layout pageType="sideBar" pendingCount={pendingCount}>

      <Box
        sx={{
          px: 2,
          color: theme.palette.text.primary,
        }}
      >

        <Box sx={{ display: 'flex', justifyContent: "space-between", alignItems: "center" }}>
          {pageTitle && (
            <Typography variant="title" sx={{ m: 0 }}>
              {pageTitle}
            </Typography>)}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: "end", alignItems: "center", }}>
            {searchBoxVisible !== undefined && <SearchComponent value={searchTerm} onSearchChange={handleSearchChange} />}
            <Tooltip title="Refresh records" arrow>
              <Button
                variant="outlined"
                onClick={handleRefreshResources}
                disabled={refreshLoading}
                sx={{
                  width: 30,
                  height: 30,
                  minWidth: 30,
                  minHeight: 30,
                  padding: "17px",
                  borderRadius: 1,
                  color: isDark ? "#81c784" : "#2e7d32",
                  borderColor: isDark ? "#81c784" : "#2e7d32",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  "& .MuiButton-startIcon": {
                    margin: 0,
                  },
                  "&:hover": {
                    borderColor: isDark ? "#a5d6a7" : "#1b5e20",
                    backgroundColor: isDark ? "rgba(129, 199, 132, 0.16)" : "#00a00b",
                    color: isDark ? "#a5d6a7" : 'white',
                  },
                }}
              >
                {refreshLoading ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <RefreshIcon fontSize="small" />
                )}
              </Button>
            </Tooltip>
          </Box>
        </Box>

        {/*  UPDATED: Container Background swaps to standard paper dynamically */}
        <Box sx={{ mt: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: "baseline", zIndex: 3, position: 'relative', p: 0.5 }}>
            <Box display="flex" gap={0.5} sx={{ pr: 2, flexWrap: 'wrap' }}>
              <Typography sx={{ fontWeight: 'Bold' }}>GSnow Status :</Typography>
              {[
                { key: 'requested', label: 'Requested', color: isDark ? '#ffb74d' : '#ed6c02', icon: <AccessTimeIcon sx={{ fontSize: 12 }} /> },
                { key: 'approved', label: 'Approved', color: isDark ? '#81c784' : '#2e7d32', icon: <CheckCircleIcon sx={{ fontSize: 12 }} /> },
                { key: 'rejected', label: 'Rejected', color: isDark ? '#e57373' : '#d32f2f', icon: <CancelIcon sx={{ fontSize: 12 }} /> },
                { key: 'not requested', label: 'Not Requested', color: isDark ? '#9e9e9e' : '#757575', icon: <RemoveCircleOutlineIcon sx={{ fontSize: 12 }} /> },
              ].map((s) => {
                const isSelected = selectedSnowStatuses.includes(s.key);
                return (
                  <Chip
                    key={s.key}
                    label={s.label}
                    title={`Filter by GSnow ${s.label}`}
                    onClick={() => handleSnowStatusToggle(s.key)}
                    variant={isSelected ? 'filled' : 'outlined'}
                    icon={isSelected ? <CheckCircleIcon sx={{ fontSize: 18, ml: 0.5, color: s.color }} /> : s.icon}
                    sx={{
                      height: '24px',
                      border: 'none',
                      backgroundColor: isSelected ? `${s.color}15` : 'transparent',
                      color: isSelected ? s.color : 'text.secondary',
                      boxShadow: isSelected
                        ? (isDark ? '0px 2px 4px rgba(0,0,0,0.4)' : '0px 2px 4px rgba(0,0,0,0.12)')
                        : 'none',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': { backgroundColor: `${s.color}25` },
                      '& .MuiChip-icon': { marginRight: '4px', ml: 1, color: isSelected ? s.color : `${s.color}CC` },
                      '& .MuiChip-label': { paddingLeft: 0 },
                    }}
                  />
                );
              })}
            </Box>
            <Box display="flex" gap={0.5} sx={{ pr: 2, flexWrap: 'wrap' }}>
              <Typography sx={{ fontWeight: 'Bold' }}>Git Status :</Typography>
              {
                [
                  { key: 'queued', label: 'Queued' },
                  { key: 'in progress', label: 'In Progress' },
                  { key: 'completed', label: 'Success' },
                  { key: 'failed', label: 'Failure' },
                  { key: 'null', label: 'Not Deployed' }
                ].map((status) => {
                  const isSelected = selectedStatuses.includes(status.key);
                  const statusColor = runningIndicatorColors[status.key];

                  return (
                    <Chip
                      key={status.key}
                      label={status.label}
                      title={`Filter by ${status.label} status`}
                      onClick={() => handleStatusToggle(status.key)}
                      variant={isSelected ? "filled" : "outlined"}
                      icon={
                        isSelected ? (
                          <CheckCircleIcon sx={{ fontSize: 18, ml: 0.5, color: statusColor }} />
                        ) : (
                          <CircleIcon sx={{ fontSize: 12, ml: 1, color: statusColor }} />
                        )
                      }
                      sx={{
                        height: '24px',
                        border: 'none',
                        backgroundColor: isSelected ? `${statusColor}15` : 'transparent',
                        color: isSelected ? statusColor : 'text.secondary',
                        boxShadow: isSelected
                          ? (isDark ? '0px 2px 4px rgba(0, 0, 0, 0.4)' : '0px 2px 4px rgba(0, 0, 0, 0.12), 0px 1px 2px rgba(0, 0, 0, 0.08)')
                          : 'none',

                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          backgroundColor: `${statusColor}25`,
                        },
                        '& .MuiChip-icon': {
                          marginRight: '4px',
                          color: isSelected ? statusColor : `${statusColor}CC`,
                        },
                        '& .MuiChip-label': {
                          paddingLeft: 0,
                        }
                      }}
                    />
                  );
                })
              }
            </Box>
          </Box>

          {/* Table Content */}
          <Box sx={{ width: '100%', mt: 0.5, height: 'calc(100vh - 185px)', overflowX: 'auto' }}>
            {tableContent}
          </Box>
        </Box>

        {/* View Dialog */}
        <Dialog
          open={openViewDialog}
          onClose={() => handleCloseViewDialog()}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              fontWeight: "bold",
              bgcolor: theme.palette.background.default,
              color: theme.palette.text.primary,
              fontSize: theme.typography.h5.fontSize,
            }}
          >
            Details
            <IconButton aria-label="close" onClick={handleCloseViewDialog}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent
            sx={{
              bgcolor: theme.palette.background.default,
              display: "flex",
              justifyContent: 'space-between',
              flexWrap: "wrap",
              //  UPDATED: Dynamic divider
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            {['General', 'Disks', 'Networking', ...Object.keys(groupedDetails || {}).filter(tab => !['General', 'Tag', 'Disks', 'Networking'].includes(tab))]
              .filter(tabName => groupedDetails[tabName] && groupedDetails[tabName].length > 0)
              .map((tabName) => (
                <Box key={tabName} sx={{ mb: 2, width: '100%' }}>
                  <Typography variant="h6" sx={{
                    mb: 1,
                    fontWeight: 'bold',
                    color: theme.palette.text.primary,
                    fontSize: theme.typography.h6.fontSize,
                    position: 'relative',
                    display: 'inline-block',
                    '&:after': {
                      content: '""',
                      display: 'block',
                      width: '50%',
                      borderBottom: `1px solid ${theme.palette.primary.main}`,
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                    },
                  }}>
                    {tabName}
                  </Typography>
                  {(groupedDetails[tabName] || []).map((detail) => {
                    const isListeners = detail.fieldName === "Listeners";
                    const isRules = detail.fieldName === "InboundRules" || detail.fieldName === "OutboundRules";
                    const isIvantiConfig = detail.fieldName === "ivantiConfiguration";

                    if (isIvantiConfig) {
                      let parsed = null;
                      try {
                        parsed = typeof detail.fieldData === "string" ? JSON.parse(detail.fieldData) : detail.fieldData;
                      } catch (e) {
                        console.error("Failed to parse ivantiConfiguration", e);
                      }

                      if (parsed && Object.keys(parsed).length > 0) {
                        return (
                          <Box key={detail.requestId || detail.fieldName} sx={{ width: "100%", mt: 2, mb: 1 }}>
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: "bold", mb: 1, color: "text.primary" }}
                            >
                              Ivanti Configuration
                            </Typography>
                            
                            {Object.entries(parsed).map(([key, val]) => {
                              let displayValue = "N/A";
                              
                              if (Array.isArray(val)) {
                                displayValue = val.map((v) => v?.text || v?.value || v).join(", ");
                              } else if (val && typeof val === "object") {
                                displayValue = val.text || val.value || "N/A";
                              } else if (val !== null && val !== undefined) {
                                displayValue = String(val);
                              }
                              
                              const formattedLabel = key.replace(/([A-Z])/g, " $1").trim();

                              return (
                                <Box
                                  key={key}
                                  sx={{
                                    display: "grid",
                                    gridTemplateColumns: "220px 1fr",
                                    alignItems: "center",
                                    py: 1,
                                    borderBottom: `1px solid ${theme.palette.divider}`,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      textTransform: "capitalize",
                                      color: "text.secondary",
                                    }}
                                  >
                                    {formattedLabel}:
                                  </Typography>
                                  
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: isDark ? "#90caf9" : "#1976d2",
                                      wordBreak: "break-word",
                                    }}
                                  >
                                    {displayValue}
                                  </Typography>
                                </Box>
                              );
                            })}
                          </Box>
                        );
                      }
                      return null;
                    }

                    if (isListeners || isRules) {
                      return (
                        <Box key={detail.requestId || detail.fieldName} sx={{ py: 1, width: "100%" }}>
                          {isListeners ? (
                            <DynamicListeners fieldData={detail.fieldData} tablefrom={true} />
                          ) : (
                            <DynamicRules fieldData={detail.fieldData} title={detail.fieldName} />
                          )}
                        </Box>
                      );
                    }

                    // --- Standard Rows ---
                    return (
                      <Box
                        key={detail.requestId || detail.fieldName}
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "220px 1fr",
                          alignItems: "center",
                          py: 1,
                          borderBottom: `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            textTransform: "capitalize",
                            color: "text.secondary",
                          }}
                        >
                          {detail.fieldName}:
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{
                            color: isDark ? "#90caf9" : "#1976d2",
                            wordBreak: "break-word",
                          }}
                        >
                          {detail.fieldData || "N/A"}
                        </Typography>
                      </Box>
                    );
                  })}

                </Box>
              ))}

            {/* Price Details Section */}
            <Box sx={{ mb: 2, width: "100%" }}>
              <Typography variant="h6" sx={{
                mb: 1,
                fontWeight: 'bold',
                color: theme.palette.text.primary,
                fontSize: theme.typography.h6.fontSize,
                position: 'relative',
                display: 'inline-block',
                '&:after': {
                  content: '""',
                  display: 'block',
                  width: '50%',
                  borderBottom: `1px solid ${theme.palette.primary.main}`,
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                },
              }}>
                Price Details 
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
                <Box>
                  <Typography variant="body2" sx={{
                    mr: 2,
                    mb: 1,
                    textTransform: "capitalize",
                    color: theme.palette.text.secondary,
                    fontSize: theme.typography.body2.fontSize,
                  }}>
                    Total Forecasted Price:
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{
                    color: theme.palette.primary.main,
                    fontSize: theme.typography.body2.fontSize,
                    fontWeight: 'bold'
                  }}>
                    {viewResponse.totalPrice ? `$${viewResponse.totalPrice}` : "N/A"}
                  </Typography>
                </Box>
              </Box>
             
            </Box>
            
            {/* Tags Section at the Bottom */}
            <Box sx={{ mb: 2, mt: 2, display: 'flex', justifyContent: 'flex-start', width: "100%", flexWrap: 'wrap' }}>
              {resourceDetails.filter(detail => detail.tabName === "Tag").map(detail => (
                <Box key={detail.requestId}>
                  <Typography variant="body2"
                    sx={{
                      fontWeight: "bold",
                      textTransform: 'capitalize',
                      fontSize: theme.typography.body2.fontSize,
                      bgcolor: isDark ? 'rgba(144, 202, 249, 0.12)' : 'rgba(223, 237, 255, 0.6)',
                      color: isDark ? '#90caf9' : theme.palette.primary.main,
                      m: 1,
                      px: 2,
                      paddingTop: "5px",
                      paddingBottom: "5px",
                      borderRadius: 5,
                      display: 'inline-block',
                      textAlign: 'center',
                    }}>
                    {detail.fieldData}
                  </Typography>
                </Box>
              ))}
            </Box>
          </DialogContent>
        </Dialog>

        {/* UnApprove Dialog */}
        <Dialog
          open={openUnApproveDialog}
          onClose={handleCloseUnApproveDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogContent sx={{ bgcolor: "background.paper" }}>
            <Box sx={{ width: '100%' }}>
              <Box sx={{ display: 'flex', mb: 1, mt: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: "bold", width: '100%', color: 'text.primary' }}>
                  Status: Assigned to Other Approver
                </Typography>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions
            sx={{
              justifyContent: "flex-end",
              gap: 1,
              bgcolor: "background.paper",
            }}
          >
            <Button onClick={handleCloseUnApproveDialog} variant="greenContained" color="primary" >
              Ok
            </Button>
          </DialogActions>
        </Dialog>

      

        <DateTimePickerDialog
          open={scheduleDialogOpen}
          onClose={handleCloseScheduleDialog}
          onConfirm={handleConfirmSchedule}
          loading={scheduleLoading}
          value={scheduleValue}
          onChange={setScheduleValue}
          title="Schedule Deployment"
          label="Schedule time"
        />

        {/* Comments Dialog */}
        <Dialog
          open={openCommentsDialog}
          onClose={handleCloseCommentsDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              bgcolor: theme.palette.background.default,
              color: theme.palette.text.primary,
              fontSize: theme.typography.h5.fontSize,
              fontWeight: 'bold'
            }}
          >
            Comments
            <IconButton aria-label="close" onClick={handleCloseCommentsDialog}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ bgcolor: theme.palette.background.default }}>
            {(() => {
              const hasComments = comments.length > 0;

              if (loadingComments) {
                return (
                  <CircularProgress
                    sx={{
                      textAlign: 'center',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      width: '100%',
                    }}
                  />
                );
              }
              if (hasComments) {
                return comments.map((comment, index) => {
                  const formattedDate = formatDistanceToNow(new Date(comment.createdDate), { addSuffix: true });
                  return (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        bgcolor: isDark ? 'background.paper' : '#f5f5f5',
                        borderRadius: 2,
                        p: 2,
                        mb: 2,
                        boxShadow: 1,
                      }}
                    >
                      <Avatar sx={{ bgcolor: 'gold', mr: 2 }}>
                        {comment.commentedBy.charAt(0)}
                      </Avatar>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          {comment.commentedBy}
                        </Typography>

                        {/*  FIXED: Invalid inline textSecondary replaced with MUI Typography */}
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1, lineHeight: 1 }}>
                          {formattedDate}
                        </Typography>

                        <Typography variant="body2" sx={{ mt: 1 }}>
                          {comment.comments}
                        </Typography>

                        {/*  FIXED: Inner nested resource box dynamically colored */}
                        <Box sx={{ mt: 1, display: 'inline-block', bgcolor: isDark ? 'rgba(25, 118, 210, 0.12)' : '#e3f2fd', borderRadius: 1, p: 0.5 }}>
                          <Typography variant="body2" sx={{ color: isDark ? '#90caf9' : '#1976d2' }}>
                            {comment.resourceName}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  );
                });
              }
              return <Typography variant="body2">No comments available.</Typography>;
            })()}
          </DialogContent>
        </Dialog>

        <Dialog open={openApprovalFlow} onClose={() => setOpenApprovalFlow(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ bgcolor: 'background.paper' }}>
            Approval Status
            <IconButton
              aria-label="close"
              onClick={() => setOpenApprovalFlow(false)}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ bgcolor: 'background.paper' }}>
            <ApprovalFlow
              steps={approvalSteps}
              isDeployed={approvalTicket?.isDeployed}
            />
          </DialogContent>
        </Dialog>

        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontWeight: 'bold',
              bgcolor: theme.palette.background.paper,
              color: theme.palette.text.primary,
              fontSize: theme.typography.h6.fontSize,
              pb: 1.5,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <img
                src={servicenow}
                alt="ServiceNow"
                style={{
                  width: '20px',
                  height: '20px',
                  objectFit: 'contain',
                  borderRadius: '2px',
                }}
              />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                }}
              >
                ServiceNow Details
              </Typography>
            </Box>
            <IconButton onClick={() => setDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ bgcolor: theme.palette.background.paper, pt: 1 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : snowDetails ? (
              <Box>
                {/* Details rows */}
                <Box sx={{ mt: 1 }}>
                  {[
                    {
                      label: 'Number',
                      value: (
                        <Link
                          // href={`https://gainwellnowdev.service-now.com/nav_to.do?uri=change_request.do?sys_id=${snowDetails.sys_ID || ''}`}
                          href={snowDetails.snowUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            color: theme.palette.primary.main,
                            textDecoration: 'underline',
                            fontSize: theme.typography.body2.fontSize,
                          }}
                        >
                          {snowDetails.changeNumber || '-'}
                        </Link>
                      ),
                    },
                    { label: 'Type', value: snowDetails.type || '-' },
                    { label: 'State', value: snowDetails.state || '-' },

                    {
                      label: 'Approval Status',
                      value: snowDetails.approvalStatus
                        ? snowDetails.approvalStatus.charAt(0).toUpperCase() +
                        snowDetails.approvalStatus.slice(1)
                        : '-'
                    },
                    {
                      label: 'GSnow Approval Due Date',
                      value: snowDetails.dueDate ? (
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                        }}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: theme.palette.primary.main,
                            }}
                          >
                            {formatDateTime(snowDetails.dueDate)}
                          </Typography>

                          <Typography
                            variant="caption"
                            sx={{
                              color: '#757575',
                              fontSize: '0.72rem',
                              lineHeight: 1.1,
                            }}
                          >
                            {getCountdownText(snowDetails.dueDate)}
                          </Typography>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <AccessTimeIcon
                            sx={{
                              fontSize: '1rem',
                              color: 'text.secondary',
                            }}
                          />
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            NA
                          </Typography>
                        </Box>
                      ),
                    },
                    {
                      label: 'Short Description',
                      value: snowDetails.shortDescription || '-',
                    },
                  ].map((item, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '170px 1fr',
                        alignItems: 'center',
                        py: 1.5,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.text.secondary,
                          fontSize: theme.typography.body2.fontSize,
                        }}
                      >
                        {item.label}:
                      </Typography>

                      {typeof item.value === 'string' ? (
                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.palette.primary.main,
                            fontSize: theme.typography.body2.fontSize,
                            wordBreak: 'break-word',
                          }}
                        >
                          {item.value}
                        </Typography>
                      ) : (
                        item.value
                      )}
                    </Box>
                  ))}
                </Box>

                {/* Description */}
                <Box sx={{ mt: 4 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 1.5,
                      fontWeight: 700,
                      color: theme.palette.text.primary,
                      fontSize: theme.typography.h6.fontSize,
                    }}
                  >
                    Description
                  </Typography>

                  <Box
                    sx={{
                      p: 2,
                      bgcolor:
                        theme.palette.mode === 'dark' ? '#2a2d3c' : '#f5f5f5',
                      borderRadius: 1,
                      minHeight: '140px',
                      maxHeight: '280px',
                      overflowY: 'auto',
                      border: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.primary,
                        fontSize: theme.typography.body2.fontSize,
                        lineHeight: 1.6,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {snowDetails.description || 'No description available'}
                    </Typography>
                  </Box>
                </Box>


                <Box sx={{
                  mt: 4,
                }}>
                  <Box
                    sx={{
                      px: 2,
                      // bgcolor: theme.palette.mode === 'dark' ? '#2a2d3c' : '#f5f5f5',
                      borderRadius: 1,
                      border: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    {[
                      { label: 'Close Code', value: snowDetails.closeCode || '-' },

                      ...(snowDetails.closeCode === 'Unsuccessful'
                        ? [
                          {
                            label: 'Close Code Unsuccessful',
                            value: snowDetails.closeCodeUnsuccessful || '-',
                          },
                        ]
                        : []),

                      { label: 'Close Note', value: snowDetails.closeNotes || '-' },
                    ].map((item, index, arr) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: '140px 1fr',
                          alignItems: 'center',
                          py: 1.5,
                          borderBottom:
                            index !== arr.length - 1
                              ? `1px solid ${theme.palette.divider}`
                              : 'none',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.palette.text.primary,
                            fontSize: theme.typography.body2.fontSize,
                            textAlign: 'left',

                          }}
                        >
                          {item.label}:
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.palette.text.main,
                            fontSize: theme.typography.body2.fontSize,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            textAlign: 'left',

                          }}
                        >
                          {item.value}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>

              </Box>
            ) : (
              <Typography sx={{ fontSize: theme.typography.body1.fontSize }}>
                No details available
              </Typography>
            )}
          </DialogContent>
        </Dialog>


        <Dialog
          open={gitDetailsDialogOpen}
          onClose={() => setGitDetailsDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ fontSize: '18px', fontWeight: 'Bold', mt: 1, bgcolor: "background.paper" }}> GitHub Run Details</DialogTitle>
          <DialogContent sx={{ bgcolor: "background.paper" }}>
            {(() => {
              const hasGitDetails = gitDetails && Object.keys(gitDetails).length > 0;
              const isFailed = gitDetails?.gitStatus?.toLowerCase().includes('failed');

              if (!hasGitDetails) {
                return <Typography variant="body2">No details found.</Typography>;
              }

              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '120px 1fr', alignItems: 'start' }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', width: 110, color: isFailed ? 'error.main' : 'text.primary' }}>
                      Status:
                    </Typography>
                    <Typography variant="body2" sx={{ color: getColorByStatus(gitDetails.gitStatus) }}>
                      {gitDetails.gitStatus}
                    </Typography>
                  </Box>

                  {/* Run ID */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: '120px 1fr', alignItems: 'center' }}>
                    <Typography variant="body2" fontWeight="bold">
                      Run ID:
                    </Typography>
                    {renderRunId(gitDetails.run_Id, gitDetails.gitWorkflowUrl)}
                  </Box>

                  {(() => {
                    const isFailed = getStatusKey(gitDetails.gitStatus) === 'failed';
                    if (!isFailed) return null;

                    return (
                      <Box sx={{ display: 'grid', gridTemplateColumns: '120px 1fr', alignItems: 'start' }}>
                        <Typography variant="body2" fontWeight="bold">
                          Error Summary:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            color: 'text.secondary'
                          }}
                        >
                          {gitDetails.gitActionMessageSummary || "No error summary available."}
                        </Typography>
                      </Box>
                    );
                  })()}

                  {/* Message */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: '120px 1fr', alignItems: 'start' }}>
                    <Typography variant="body2" fontWeight="bold">
                      Message:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        color: 'text.secondary'
                      }}
                    >
                      {gitDetails.gitActionResouceHandlerMessage || "No message available."}
                    </Typography>
                  </Box>
                </Box>
              );
            })()}
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 3, bgcolor: "background.paper" }}>
            <Button
              onClick={() => setGitDetailsDialogOpen(false)}
              variant="contained"
              color="primary"
            >
              OK
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
}